import { useContext } from 'react';
import { BaseWidget } from './BaseWidget';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import { SelectControl } from '../controls/SelectControl';
import { ColorControl } from '../controls/ColorControl';
import { FontControl } from '../controls/grouped/FontControl';
import { db } from '../../../../firebase/config';
import { client } from '../../../../hooks/Client';
import { useFirestoreGeneral } from '../../../../firebase/useFirestore';
import { TemplateDataContext } from '../context/TemplateDataContext';
import PublicReportContext from '../../../../StateManagment/PublicReportContext';
import { fetchTemplateData } from '../utils/fetchTemplateData';
import WidgetSourcePlaceholder from '../components/WidgetSourcePlaceholder';
import LoadingSpinner from '../../../common/LoadingSpinner';
import notDeleted from '../../../../helpers/notDeleted';

// Short label for the picker — anecdotes run long, so truncate.
const anecdoteLabel = (a) => {
    const text = (a.Anecdote || '').trim();
    if (!text) return 'Anekdote zonder tekst';
    return text.length > 50 ? `${text.slice(0, 50)}…` : text;
};

/**
 * AnecdoteWidget — picks one Storytelling anecdote and renders its text.
 * Data resolves per mode, mirroring the other data widgets:
 *   - template preview → templateData.data.anecdotes (exportTemplateData gatherer)
 *   - live (editor/own tenant) → Firestore `Anecdotes` by ID
 *
 * Shows the text only, no author. v1 reports rendered anecdotes that way
 * (quotes carried a name, anecdotes did not) and the Storytelling tab does the
 * same, so adding an author here would invent an attribution nobody wrote.
 *
 * Built for the v1 → v2 report conversion.
 * See docs/features/livereport-v1-to-v2-conversion.md
 *
 * @extends {BaseWidget}
 */
export class AnecdoteWidget extends BaseWidget {
    getName() { return 'Anekdote'; }
    getCategory() { return 'research'; }
    getIcon() { return <AutoStoriesIcon />; }

    getControls() {
        return {
            anecdoteId: new SelectControl({
                label: 'Anekdote',
                description: 'Kies een anekdote',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const blob = await fetchTemplateData();
                        values = blob?.data?.anecdotes || [];
                    } else {
                        /* Equality + orderBy on a different field needs a composite
                           index that Anecdotes doesn't have, so sort client-side. */
                        const snap = await db.collection('Anecdotes')
                            .where('CompagnyID', '==', client || '')
                            .get();
                        values = snap.docs
                            .map(doc => doc.data())
                            .filter(notDeleted)
                            .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
                    }

                    return {
                        choices: values.map(a => ({ value: a.ID, label: anecdoteLabel(a) })),
                        allowSearch: true,
                    };
                },
            }),
            font: new FontControl({
                label: 'Lettertype',
                description: 'Instellingen voor het lettertype van de anekdote',
                defaultValue: {
                    fontSize: { value: 16, unit: 'px' },
                    alignment: 'left',
                },
            }),
            color: new ColorControl({
                label: 'Kleur',
                description: 'Kleur van de tekst',
                defaultValue: '#000000',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.anecdoteId - Selected Anecdotes ID
 * @param {object} [props.font] - FontControl value
 * @param {string} [props.color] - Text colour
 */
const Renderer = ({ anecdoteId, font, color }) => {
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);

    // Op een gedeelde link (geen login) komt de tekst mee met getPublicLiveReport2:
    // Anecdotes is alleen voor het eigen team leesbaar. Daar dus niet zelf lezen.
    // AI-CONTEXT: See docs/features/public-livereport-data.md (fase 3)
    const publicReport = useContext(PublicReportContext);
    const isPublic = Boolean(publicReport);
    const liveAnecdotes = useFirestoreGeneral('Anecdotes', 'ID', (isTemplate || isPublic) ? '' : (anecdoteId || ''));
    const anecdote = isTemplate
        ? (templateData?.data?.anecdotes || []).find(a => a.ID === anecdoteId)
        : isPublic
            ? (publicReport.storytelling?.anecdotes || []).find(a => a.ID === anecdoteId)
            : (Array.isArray(liveAnecdotes) && liveAnecdotes[0]);

    /* Gate on the document, never on its text: the Storytelling tab creates
       rows with `Anecdote: ''`, and treating those as "no source" would throw
       the user's selection away and show the CTA on a perfectly valid pick. */
    if (!anecdote) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* Op een gedeelde link is alles al binnen met de pagina: wat ontbreekt
           hoort niet bij dit rapport, niet 'nog onderweg'. */
        const stillResolving = isTemplate
            ? !templateData
            : !isPublic && (anecdoteId && !Array.isArray(liveAnecdotes));
        if (stillResolving) return <LoadingSpinner />;
        return <WidgetSourcePlaceholder ctaLabel="Selecteer anekdote" />;
    }

    /* Child is a <span>: layout.css sets `color: var(--ink)` on bare h3/p/div,
       which beats an inherited colour and would silently kill the colour control.
       AI-CONTEXT: See docs/pitfalls.md#ui-scoped-rule-lands-on-a-global-element-rule */
    const style = {
        textAlign: font?.alignment,
        fontFamily: font?.fontFamily,
        fontSize: font?.fontSize && `${font.fontSize.value}${font.fontSize.unit}`,
        fontStyle: font?.fontStyle,
        fontWeight: font?.fontWeight,
        textDecoration: font?.textDecoration,
        color: color,
    };

    return (
        <div className="livereport-anecdote" style={style}>
            <span className="livereport-anecdote-text">{anecdote.Anecdote}</span>
        </div>
    );
};
